import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db, callFunction } from '../firebase';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';
import { formatGmd, formatUsd } from '../billingTiers';

function CompanyUsers() {
    const { session, loading } = useCompanySession();
    const [users, setUsers] = useState([]);
    const [billing, setBilling] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [brokerForm, setBrokerForm] = useState({ email: '', password: '', displayName: '', branch: 'Head Office' });
    const [form, setForm] = useState({
        email: '',
        password: '',
        displayName: '',
        role: 'manager',
        department: '',
        branch: 'Head Office'
    });

    const loadUsers = async (tenantId) => {
        const snap = await getDocs(collection(db, 'tenants', tenantId, 'users'));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setUsers(list);
        const tenantSnap = await getDoc(doc(db, 'tenants', tenantId));
        setBilling(tenantSnap.exists() ? (tenantSnap.data().billing || null) : null);
    };

    useEffect(() => {
        if (session?.tenantId) loadUsers(session.tenantId);
    }, [session]);

    const grantAccess = async (payload) => {
        setBusy(true);
        setMessage('');
        try {
            await callFunction('createTenantUser', { ...payload, tenantId: session.tenantId });
            setMessage('Connected ' + payload.email + ' to ' + session.companyName);
            await loadUsers(session.tenantId);
            return true;
        } catch (err) {
            setMessage(err.message || 'Could not create user');
            return false;
        } finally {
            setBusy(false);
        }
    };

    const handleConnectBroker = async (e) => {
        e.preventDefault();
        const ok = await grantAccess({
            ...brokerForm,
            role: 'broker',
            department: 'Sales / Broking'
        });
        if (ok) setBrokerForm({ email: '', password: '', displayName: '', branch: 'Head Office' });
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        const ok = await grantAccess(form);
        if (ok) setForm({ email: '', password: '', displayName: '', role: 'manager', department: '', branch: 'Head Office' });
    };

    if (loading || !session) return <div className="p-5">Loading team...</div>;
    if (session.role !== 'admin') {
        return (
            <CompanyLayout session={session}>
                <div className="alert alert-warning">Only the company IT admin can grant department access.</div>
            </CompanyLayout>
        );
    }

    const brokers = users.filter((u) => u.role === 'broker');

    return (
        <CompanyLayout session={session}>
            <h3 className="mb-3">Department access</h3>
            <p className="text-muted">Connect brokers and staff to {session.companyName}. They sign in at the staff login and work this company’s book.</p>
            {billing?.includedSeats ? (
                <p className={'small ' + (users.length > billing.includedSeats ? 'text-danger' : 'text-muted')}>
                    {billing.tierName} package: {users.length} / {billing.includedSeats} included seats.
                    {users.length > billing.includedSeats
                        ? ' Extra seats are ' + formatGmd(billing.extraSeatUsd) + ' (' + formatUsd(billing.extraSeatUsd) + ') each — Pulse admin can raise the package.'
                        : ''}
                </p>
            ) : null}
            {message && <div className="alert alert-info">{message}</div>}

            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <h6>Connect a broker</h6>
                    <p className="small text-muted">Creates or links a broker login. They use the staff portal and only see this company’s customers and policies.</p>
                    <form className="row g-2" onSubmit={handleConnectBroker}>
                        <div className="col-md-4"><input className="form-control" placeholder="Broker name" value={brokerForm.displayName} onChange={(e) => setBrokerForm({ ...brokerForm, displayName: e.target.value })} required /></div>
                        <div className="col-md-4"><input type="email" className="form-control" placeholder="Broker email" value={brokerForm.email} onChange={(e) => setBrokerForm({ ...brokerForm, email: e.target.value })} required /></div>
                        <div className="col-md-4"><input type="password" className="form-control" placeholder="Temporary password" value={brokerForm.password} onChange={(e) => setBrokerForm({ ...brokerForm, password: e.target.value })} required minLength={8} /></div>
                        <div className="col-12"><button className="btn btn-success" disabled={busy}>{busy ? 'Saving...' : 'Connect broker'}</button></div>
                    </form>
                    <div className="mt-3 small">
                        <strong>Connected brokers:</strong>{' '}
                        {brokers.length === 0 ? 'None yet.' : brokers.map((b) => b.email).join(', ')}
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <h6>Grant other department access</h6>
                    <form className="row g-2" onSubmit={handleCreate}>
                        <div className="col-md-6"><input className="form-control" placeholder="Display name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} required /></div>
                        <div className="col-md-6"><input type="email" className="form-control" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
                        <div className="col-md-4"><input type="password" className="form-control" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} /></div>
                        <div className="col-md-4">
                            <select className="form-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                                <option value="admin">Company admin</option>
                                <option value="manager">Manager</option>
                                <option value="underwriter">Underwriter</option>
                                <option value="broker">Broker</option>
                                <option value="claims">Claims</option>
                                <option value="finance">Finance</option>
                                <option value="audit">Audit</option>
                            </select>
                        </div>
                        <div className="col-md-4"><input className="form-control" placeholder="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
                        <div className="col-12"><button className="btn btn-primary" disabled={busy}>{busy ? 'Saving...' : 'Create user'}</button></div>
                    </form>
                </div>
            </div>
            <div className="card border-0 shadow-sm">
                <div className="card-body table-responsive">
                    <table className="table mb-0">
                        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Department</th></tr></thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id}><td>{u.displayName}</td><td>{u.email}</td><td>{u.role}</td><td>{u.department || u.branch || '—'}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </CompanyLayout>
    );
}

export default CompanyUsers;
